import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { z } from 'zod';
import { toasterApi } from '@terra/ui-design-system';
import { aiApplicationsClient } from '@terra/shared-rest-clients-ai-applications';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';
import type { AiDiscoverySummarySchema } from '@terra/shared-contracts-ai-applications';
import {
  createApiHeaders,
  withOrchestratorClient,
} from '../../clients/tester-client/api-core';
import { usePermissions } from '../usePermissions';
import { AI_APPLICATION_KEY } from './useAiApplication';
import { AI_APPLICATION_URL_EDIT_RESTRICTION_KEY } from './useAiApplicationUrlEditRestriction';
import {
  AGENT_EXECUTIONS_BY_TARGET_KEY,
  fetchAgentExecutionsByTarget,
} from './useAgentExecutionsByTarget';

type AiDiscoverySummary = z.infer<typeof AiDiscoverySummarySchema>;

const withHeaders = async () =>
  withOrchestratorClient(await createApiHeaders());

export const AI_DISCOVERY_SUMMARY_KEY = 'discovery-summary';

function isDiscoverySummaryLive(
  summary: AiDiscoverySummary | undefined,
): boolean {
  return (
    summary != null &&
    (summary.discoveryStatus === 'RUNNING' ||
      summary.awaitingCodeDiscovery ||
      summary.pendingProbeCount > 0 ||
      summary.activeExecutionCount > 0)
  );
}

const DEFAULT_LIVE_REFETCH_INTERVAL_MS = 5_000;

function resolveAiDiscoveryRefetchInterval({
  refetchInterval,
  shouldPollWhileLive,
}: {
  refetchInterval: number | false | undefined;
  shouldPollWhileLive: boolean;
}): number | false {
  if (refetchInterval === false) {
    return false;
  }

  if (typeof refetchInterval === 'number') {
    return refetchInterval;
  }

  return shouldPollWhileLive ? DEFAULT_LIVE_REFETCH_INTERVAL_MS : false;
}

const ClientErrorBodySchema = z.object({
  message: z.string().min(1),
});

function clientErrorMessage(body: unknown): string | undefined {
  const parsed = ClientErrorBodySchema.safeParse(body);

  return parsed.success ? parsed.data.message : undefined;
}

function assertDiscoveryRequestSucceeded<TBody>(response: {
  status: number;
  body: TBody;
}): asserts response is { status: 200; body: TBody } {
  if (response.status === 200) {
    return;
  }

  throw new Error(clientErrorMessage(response.body) ?? '');
}

function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }

  return undefined;
}

export function useAiDiscoverySummary({
  aiApplicationId,
  enabled = true,
  refetchInterval,
}: {
  aiApplicationId: string;
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  const t = useTranslations('discovery.aiApplication');
  const queryClient = useQueryClient();
  const previousDiscoveryStatusRef = useRef<string | undefined>(undefined);
  const previousDiscoveryRunIdRef = useRef<string | undefined>(undefined);
  const query = useQuery({
    queryKey: [AI_APPLICATION_KEY, aiApplicationId, AI_DISCOVERY_SUMMARY_KEY],
    queryFn: async () => {
      const response = await aiApplicationsClient.discoverySummary({
        params: { aiApplicationId },
        ...(await withHeaders()),
      });

      if (response.status !== 200) {
        throw new Error(t('summaryError'));
      }

      return response.body;
    },
    enabled: enabled && Boolean(aiApplicationId),
    refetchInterval: (currentQuery) =>
      resolveAiDiscoveryRefetchInterval({
        refetchInterval,
        shouldPollWhileLive: isDiscoverySummaryLive(currentQuery.state.data),
      }),
  });

  useEffect(() => {
    const discoveryStatus = query.data?.discoveryStatus;
    const discoveryRunId = query.data?.discoveryRunId;

    if (
      previousDiscoveryStatusRef.current === 'RUNNING' &&
      (discoveryStatus === 'COMPLETE' || discoveryStatus === 'FAILED')
    ) {
      void queryClient.invalidateQueries({
        queryKey: [AI_APPLICATION_KEY, aiApplicationId],
      });
      void queryClient.invalidateQueries({
        queryKey: [AGENT_EXECUTIONS_BY_TARGET_KEY, aiApplicationId],
      });
    }

    if (
      discoveryRunId != null &&
      previousDiscoveryRunIdRef.current != null &&
      previousDiscoveryRunIdRef.current !== discoveryRunId
    ) {
      void queryClient.invalidateQueries({
        queryKey: [AGENT_EXECUTIONS_BY_TARGET_KEY, aiApplicationId],
      });
    }

    previousDiscoveryStatusRef.current = discoveryStatus;
    previousDiscoveryRunIdRef.current = discoveryRunId ?? undefined;
  }, [
    aiApplicationId,
    query.data?.discoveryRunId,
    query.data?.discoveryStatus,
    queryClient,
  ]);

  return {
    discoverySummary: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useAiDiscoveryExecutions({
  applicationId,
  enabled = true,
  refetchInterval,
  limit = 200,
}: {
  applicationId: string;
  enabled?: boolean;
  refetchInterval?: number | false;
  limit?: number;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [
      AGENT_EXECUTIONS_BY_TARGET_KEY,
      applicationId,
      'AI_DISCOVERY',
      limit,
    ],
    queryFn: () =>
      fetchAgentExecutionsByTarget({
        targetId: applicationId,
        skip: 0,
        limit,
        executionType:
          'AI_DISCOVERY' satisfies AgentExecutionDto['executionType'],
      }),
    enabled: enabled && Boolean(applicationId),
    refetchInterval: (currentQuery) => {
      const hasLiveExecution = (currentQuery.state.data?.executions ?? []).some(
        (execution) =>
          execution.status === 'PENDING' || execution.status === 'RUNNING',
      );
      const summary = queryClient.getQueryData<AiDiscoverySummary>([
        AI_APPLICATION_KEY,
        applicationId,
        AI_DISCOVERY_SUMMARY_KEY,
      ]);

      return resolveAiDiscoveryRefetchInterval({
        refetchInterval,
        shouldPollWhileLive:
          hasLiveExecution || isDiscoverySummaryLive(summary),
      });
    },
  });

  return {
    ...query,
    executions: query.data?.executions ?? [],
  };
}

export function useTriggerAiDiscovery({
  aiApplicationId,
}: {
  aiApplicationId: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await aiApplicationsClient.discover({
        params: { aiApplicationId },
        ...(await withHeaders()),
      });

      assertDiscoveryRequestSucceeded(response);

      return response.body;
    },
    onSuccess: async (result) => {
      if (result.status === 'started') {
        await queryClient.refetchQueries({
          queryKey: [
            AI_APPLICATION_KEY,
            aiApplicationId,
            AI_DISCOVERY_SUMMARY_KEY,
          ],
        });
      }

      void queryClient.invalidateQueries({
        queryKey: [AI_APPLICATION_KEY, aiApplicationId],
      });
      void queryClient.invalidateQueries({
        queryKey: [AI_APPLICATION_URL_EDIT_RESTRICTION_KEY, aiApplicationId],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          AI_APPLICATION_KEY,
          aiApplicationId,
          AI_DISCOVERY_SUMMARY_KEY,
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [AGENT_EXECUTIONS_BY_TARGET_KEY, aiApplicationId],
      });
    },
  });
}

export function useStopAiDiscovery({
  aiApplicationId,
}: {
  aiApplicationId: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await aiApplicationsClient.stopDiscovery({
        params: { aiApplicationId },
        ...(await withHeaders()),
      });

      assertDiscoveryRequestSucceeded(response);

      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [AI_APPLICATION_KEY, aiApplicationId],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          AI_APPLICATION_KEY,
          aiApplicationId,
          AI_DISCOVERY_SUMMARY_KEY,
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [AGENT_EXECUTIONS_BY_TARGET_KEY, aiApplicationId],
      });
    },
  });
}

export function useAiDiscoveryRunControls({
  aiApplicationId,
}: {
  aiApplicationId: string;
}): {
  runDiscovery: () => void;
  stopDiscovery: (options?: { onStopped?: () => void }) => void;
  isRunPending: boolean;
  isStopPending: boolean;
} {
  const t = useTranslations('discovery.aiApplication');
  const triggerDiscoveryMutation = useTriggerAiDiscovery({ aiApplicationId });
  const stopDiscoveryMutation = useStopAiDiscovery({ aiApplicationId });

  const runDiscovery = useCallback(() => {
    void triggerDiscoveryMutation
      .mutateAsync()
      .then((result) => {
        if (result.status === 'already_running') {
          toasterApi.info({ title: t('alreadyRunning') });

          return;
        }

        toasterApi.success({ title: t('started') });
      })
      .catch((error: unknown) => {
        toasterApi.error({
          title: getErrorMessage(error) ?? t('startFailed'),
        });
      });
  }, [t, triggerDiscoveryMutation]);

  const stopDiscovery = useCallback(
    (options?: { onStopped?: () => void }) => {
      void stopDiscoveryMutation
        .mutateAsync()
        .then(() => {
          options?.onStopped?.();
        })
        .catch((error: unknown) => {
          toasterApi.error({
            title: getErrorMessage(error) ?? t('stopFailed'),
          });
        });
    },
    [stopDiscoveryMutation, t],
  );

  return {
    runDiscovery,
    stopDiscovery,
    isRunPending: triggerDiscoveryMutation.isPending,
    isStopPending: stopDiscoveryMutation.isPending,
  };
}

export function useRetryAiDiscoveryProbe({
  aiApplicationId,
}: {
  aiApplicationId: string;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (candidateId: string) => {
      const response = await aiApplicationsClient.retryDiscoveryProbe({
        params: { aiApplicationId, candidateId },
        ...(await withHeaders()),
      });

      assertDiscoveryRequestSucceeded(response);

      return response.body;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          AI_APPLICATION_KEY,
          aiApplicationId,
          AI_DISCOVERY_SUMMARY_KEY,
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [AGENT_EXECUTIONS_BY_TARGET_KEY, aiApplicationId],
      });
    },
  });
}

export type RetryDiscoveryProbeOptions = {
  onStarted?: () => void;
};

export function useRetryDiscoveryProbeAction({
  aiApplicationId,
}: {
  aiApplicationId: string;
}): {
  onRetryProbe:
    | ((candidateId: string, options?: RetryDiscoveryProbeOptions) => void)
    | undefined;
  isRetryPending: boolean;
} {
  const t = useTranslations('discovery.aiApplication');
  const { canManageApplications } = usePermissions();
  const retryProbeMutation = useRetryAiDiscoveryProbe({ aiApplicationId });

  const retryProbe = useCallback(
    (candidateId: string, options?: RetryDiscoveryProbeOptions) => {
      void retryProbeMutation
        .mutateAsync(candidateId)
        .then((result) => {
          if (result.status === 'already_pending') {
            toasterApi.info({ title: t('retryAlreadyPending') });

            return;
          }

          toasterApi.success({ title: t('retryStarted') });
          options?.onStarted?.();
        })
        .catch((error: unknown) => {
          toasterApi.error({
            title: getErrorMessage(error) ?? t('retryFailed'),
          });
        });
    },
    [retryProbeMutation, t],
  );

  return {
    onRetryProbe: canManageApplications ? retryProbe : undefined,
    isRetryPending: retryProbeMutation.isPending,
  };
}
